import React, { useState } from 'react';

import { Modal } from 'antd';
import Axios from 'axios';
import { Formik, Field } from 'formik';

import styled from 'styled-components';

import { showDateNoTime } from '@libs/NwMoment';

import { forceReloadTransaction } from '@hooks/API/UseTransaction';
import useTransactionLockedStatus from '@hooks/Utils/UseTransactionLockedStatus';

import { Loading } from '@components/Gui/Loading';
import { NwCancelButton, NwSaveButton } from '@components/Gui/NwButton';
import NwDrawer from '@components/Gui/NwDrawer';
import NwDropDownMenu from '@components/Gui/NwDropDownMenu';
import { NWInput, NWSwitch, NWTextAreaCharCounter } from '@components/Gui/NwForm/NwFormItems';
import { NwForm, NwFormButtonsBar } from '@components/Gui/NwForm/NwFormWrapper';
import TransactionEvent from '@components/Transactions/TransactionEvent';
import TransactionStatusBreadcrumbs from '@components/Transactions/TransactionStatusBreadcrumbs';
import TransactionTitle from '@components/Transactions/TransactionTitle';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const Container = styled.div`
    padding: 8px 0;
    //border-bottom: 1px solid ${(props) => props.theme.LighterBorderColor};

    .transaction-details {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-top: 1rem;

        .transaction-details-left {
            flex: 1 1 0;

            .transaction-title {
                margin-bottom: 0.5rem;
            }
            p {
                margin-bottom: 0.5rem;
            }
        }
        .transaction-details-right {
            flex: 0 0 4rem;
        }
    }
`;

const TransactionItemDetailForm = ({ transaction, onUpdateTransaction, onTransactionRemoved }) => {
    const [editable, setEditable] = useState(false);
    const [touchForm, setTouchForm] = useState(false);

    const lock = useTransactionLockedStatus(transaction);

    const submitForm = async (values, setStatus, setSubmitting) => {
        setSubmitting(true);
        let data = {
            transactionId: transaction.ID,
            title: values.Title,
            description: values.Description,
            updateStatements: values.UpdateStatements,
        };

        try {
            await Axios.put(`/transactions/${transaction.Family}`, data);
            forceReloadTransaction(transaction.ID);
            onUpdateTransaction();
            setTouchForm(false);
            setSubmitting(false);
            setEditable(false);
        } catch (error) {
            //TODO: manage error on api call, may be by adding an ErrorMessage to the state and a condition to show it in render.
            setSubmitting(false);
            console.error('errore', error);
        }
    };

    const handleDeleteTransaction = async () => {
        try {
            await Axios.delete(`/transactions/${transaction.Family}/${transaction.ID}`);
            onTransactionRemoved();
        } catch (error) {
            Modal.error({
                title: error.response.data.Message,
            });
            console.error('Delete transaction error => ', error);
        }
    };

    const descriptions = [];
    if (transaction) {
        descriptions.push(transaction.Description);
        transaction.ClientStatements.forEach((statement) => {
            if (statement.Description) {
                descriptions.push(statement.Description);
            }
        });
        transaction.ModelStatements.forEach((statement) => {
            if (statement.Description) {
                descriptions.push(statement.Description);
            }
        });
        transaction.AgencyStatements.forEach((statement) => {
            if (statement.Description) {
                descriptions.push(statement.Description);
            }
        });
        transaction.ServiceStatements.forEach((statement) => {
            if (statement.Description) {
                descriptions.push(statement.Description);
            }
        });
    }
    const uniquedescriptions = [...new Set(descriptions)];

    let initialValues = {
        Title: transaction ? transaction.Title : '',
        Label: transaction ? transaction.Label : '',
        Description: transaction ? transaction.Description : '',
        WorkDate: transaction ? transaction.WorkDate : '',
        UpdateStatements: uniquedescriptions.length <= 1 ? true : false,
    };

    const getTransactionStatus = () => {
        if (transaction.ClientStatements.length > 0) {
            const mainLine = transaction.ClientStatements.find((statement) => !statement.isCommission);
            if (mainLine) {
                return mainLine.Status;
            }
            return transaction.ClientStatements[0].Status;
        }
        if (transaction.ModelStatements.length > 0) {
            const mainLine = transaction.ModelStatements.find((statement) => !statement.isCommission);
            if (mainLine) {
                return mainLine.Status;
            }
            return transaction.ModelStatements[0].Status;
        }
        if (transaction.AgencyStatements.length > 0) {
            return transaction.AgencyStatements[0].Status;
        }
        if (transaction.ServiceStatements.length > 0) {
            return transaction.ServiceStatements[0].Status;
        }
        return 'Billable';
    };

    //const transactionLockStatus = isLocked(transaction)

    return transaction ? (
        <Container>
            <TransactionEvent transaction={transaction} />
            <div className="transaction-details">
                <div className="transaction-details-left">
                    <TransactionTitle style={{ marginTop: '.5rem' }} transaction={transaction} />
                    <p>{transaction.Description}</p>
                    <p>
                        <strong>
                            {transaction.WorkDate ? `work date: ${showDateNoTime(transaction.WorkDate)} ` : ''}
                        </strong>
                    </p>
                </div>
                <div className="transaction-details-right">
                    <NwDropDownMenu
                        size="small"
                        actions={[
                            {
                                name: 'edit',
                                key: 'edit',
                                pinned: true,
                                icon: byPrefixAndName.fasl['pen'],
                                action: () => setEditable(true),
                            },
                            {
                                name: 'delete',
                                pinned: true,
                                disabled: lock.isLocked,
                                type: 'delete',
                                key: 'delete',
                                icon: byPrefixAndName.fasl['trash-alt'],
                                confirmationPlacement: 'left',
                                action: handleDeleteTransaction,
                            },
                        ]}
                    />
                </div>
            </div>
            {transaction.ClientStatements.length > 0 && (
                <TransactionStatusBreadcrumbs
                    onStatusChange={onUpdateTransaction}
                    status={getTransactionStatus()}
                    transactionFamily={transaction.Family}
                    transactionID={transaction.ID}
                    transactionProjectId={transaction.ProjectID}
                />
            )}
            {editable && (
                <NwDrawer
                    touchForm={touchForm}
                    onTouchForm={setTouchForm}
                    title="Edit Transaction"
                    onClose={() => {
                        setEditable(false);
                    }}
                >
                    <Formik
                        initialValues={initialValues}
                        enableReinitialize={true}
                        onSubmit={(values, { setStatus, setSubmitting }) => {
                            setStatus();
                            submitForm(values, setStatus, setSubmitting);
                        }}
                    >
                        {({ isSubmitting, values, handleSubmit }) => {
                            return (
                                <NwForm
                                    values={values}
                                    onTouchForm={setTouchForm}
                                    onFinish={handleSubmit}
                                    layout="vertical"
                                >
                                    <Field
                                        component={NWInput}
                                        label="Title"
                                        name="Title"
                                        value={values.Title}
                                        required
                                        type="text"
                                        maxLength={255}
                                    />
                                    <Field
                                        component={NWTextAreaCharCounter}
                                        label="Description"
                                        name="Description"
                                        type="text"
                                        value={values.Description}
                                        maxLength={1000}
                                        autoSize={{ minRows: 3, maxRows: 5 }}
                                    />
                                    <Field
                                        component={NWSwitch}
                                        label=""
                                        text="Update description on all statement lines"
                                        name="UpdateStatements"
                                        checked={Boolean(values.UpdateStatements)}
                                    />
                                    <NwFormButtonsBar
                                        left={
                                            <NwCancelButton
                                                disabled={isSubmitting}
                                                onClick={() => {
                                                    setTouchForm(false);
                                                    setEditable(false);
                                                }}
                                            />
                                        }
                                        right={
                                            <NwSaveButton
                                                htmlType="submit"
                                                disabled={isSubmitting}
                                                loading={isSubmitting}
                                            />
                                        }
                                    />
                                </NwForm>
                            );
                        }}
                    </Formik>
                </NwDrawer>
            )}
        </Container>
    ) : (
        <Loading />
    );
};

export default TransactionItemDetailForm;
